/**
 * Ping Service - Keeps the server alive on hosting platforms like Render
 * Prevents the app from going offline due to inactivity timeouts
 */

class PingService {
  constructor() {
    this.intervalId = null;
    this.pingInterval = 10 * 60 * 1000; // 10 minutes in milliseconds
    this.isActive = false;
    this.isVisible = true;
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);

    // Only enable ping service in production (on Render)
    this.enabled = process.env.NODE_ENV === 'production' ||
                   window.location.hostname.includes('render.com') ||
                   window.location.hostname.includes('onrender.com');
  }

  /**
   * Handle page visibility changes to pause/resume pinging
   */
  handleVisibilityChange() {
    this.isVisible = !document.hidden;
    if (this.isVisible && this.isActive) {
      // Page became visible, ping immediately to ensure server is still alive
      this.ping();
    }
  }

  /**
   * Start the ping service
   */
  start() {
    if (!this.enabled) {
      console.log('🔄 Ping service disabled (not running on Render/production)');
      return;
    }

    if (this.isActive) {
      console.log('🔄 Ping service already running');
      return;
    }

    console.log('🚀 Starting ping service - keeping server alive every 10 minutes');

    this.isActive = true;

    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Ping immediately on start
    this.ping();

    // Set up interval for regular pings
    this.intervalId = setInterval(() => {
      // Only ping if page is visible to avoid unnecessary requests
      if (this.isVisible) {
        this.ping();
      }
    }, this.pingInterval);
  }

  /**
   * Stop the ping service
   */
  stop() {
    if (!this.isActive) {
      console.log('⏹️ Ping service already stopped');
      return;
    }

    console.log('⏹️ Stopping ping service');
    this.isActive = false;

    // Remove visibility listener
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Send a ping request to the server
   */
  async ping() {
    try {
      // Use the dedicated health check endpoint
      const response = await fetch('/health', {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Server ping successful at ${new Date().toLocaleTimeString()} (uptime: ${Math.round(data.uptime)}s)`);
      } else {
        console.warn(`⚠️ Server ping failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Server ping error:', error);
    }
  }

  /**
   * Check if the service is currently active
   */
  isRunning() {
    return this.isActive;
  }

  /**
   * Get the current ping interval in minutes
   */
  getIntervalMinutes() {
    return this.pingInterval / (60 * 1000);
  }

  /**
   * Update the ping interval (in minutes)
   */
  setIntervalMinutes(minutes) {
    if (minutes < 1) {
      console.warn('⚠️ Ping interval must be at least 1 minute');
      return;
    }

    const wasActive = this.isActive;
    this.stop();

    this.pingInterval = minutes * 60 * 1000;

    if (wasActive) {
      this.start();
    }

    console.log(`🔄 Ping interval updated to ${minutes} minutes`);
  }

  /**
   * Force an immediate ping (useful for testing)
   */
  pingNow() {
    console.log('🔄 Manual ping requested');
    this.ping();
  }

  /**
   * Get service status information
   */
  getStatus() {
    return {
      enabled: this.enabled,
      active: this.isActive,
      intervalMinutes: this.getIntervalMinutes(),
      isVisible: this.isVisible,
      environment: process.env.NODE_ENV || 'development',
      hostname: window.location.hostname
    };
  }
}

// Create a singleton instance
const pingService = new PingService();

export default pingService;
